import {
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  createContext,
  useContext,
  useRef,
  useState,
} from 'react'
import { BaseCalculations } from '../../../interfaces/baseCalculation/BaseCalculations'
import { AggTranslations } from '../../../interfaces/grids/performance/AggTranslation'
import { MonthColumn } from '../../../interfaces/grids/cashflow/MonthColumns'
import { AgGridReact } from 'ag-grid-react'
import { GridRows } from '../../../interfaces/grids/GridRows'
import { DatesInfo } from '../../../interfaces/baseCalculation/DatesInfo'
import { MonthColumnIds } from '../../../interfaces/grids/cashflow/MonthColumnIds'
import { ProjectAttributes } from '../../../interfaces/project/ProjectAttributes'

type ProjektprognosSheetContextProviderProps = {
  children: ReactNode
}

export type ProjektprognosSheetContextStates = {
  cashflowGridRef: RefObject<AgGridReact>
  cashflowRows: GridRows | undefined
  setCashflowRows: Dispatch<SetStateAction<GridRows | undefined>>
  monthColumns: { [key: string]: MonthColumn }
  setMonthColumns: Dispatch<SetStateAction<{ [key: string]: MonthColumn }>>
  monthColumnIds: MonthColumnIds
  setMonthColumnIds: Dispatch<SetStateAction<MonthColumnIds>>

  performanceGridRef: RefObject<AgGridReact>
  performanceRows: GridRows | undefined
  setPerformanceRows: Dispatch<SetStateAction<GridRows | undefined>>
  performanceTotals: AggTranslations | undefined
  setPerformanceTotals: Dispatch<SetStateAction<AggTranslations | undefined>>

  projectAttributes: ProjectAttributes
  setProjectAttributes: Dispatch<SetStateAction<ProjectAttributes>>
  baseCalculations: BaseCalculations
  setBaseCalculations: Dispatch<SetStateAction<BaseCalculations>>
  datesInfo: DatesInfo
  setDatesInfo: Dispatch<SetStateAction<DatesInfo>>

  lockedBudget: boolean
  setLockedBudget: Dispatch<SetStateAction<boolean>>
  initiallyUnsetBuildStartMonth: boolean
  setInitiallyUnsetBuildStartMonth: Dispatch<SetStateAction<boolean>>

  // Account filters - separate for cashflow and performance
  selectedCashflowAccountIds: Set<number>
  setSelectedCashflowAccountIds: Dispatch<SetStateAction<Set<number>>>
  selectedPerformanceAccountIds: Set<number>
  setSelectedPerformanceAccountIds: Dispatch<SetStateAction<Set<number>>>
  filterActive: boolean
  setFilterActive: Dispatch<SetStateAction<boolean>>

  // Diff filtering - common for both cashflow and performance
  minDiff: number
  setMinDiff: Dispatch<SetStateAction<number>>
  enableDiffFilter: boolean
  setEnableDiffFilter: Dispatch<SetStateAction<boolean>>
  enableCommentFilter: boolean
  setEnableCommentFilter: Dispatch<SetStateAction<boolean>>

  // Header aggregering toggle
  hideHeaderAggregation: boolean
  setHideHeaderAggregation: Dispatch<SetStateAction<boolean>>
}

export const ProjektprognosSheetContext =
  createContext<ProjektprognosSheetContextStates | null>(null)

export const ProjektprognosSheetContextProvider = ({
  children,
}: ProjektprognosSheetContextProviderProps) => {
  const cashflowGridRef = useRef<AgGridReact>(null)
  const [cashflowRows, setCashflowRows] = useState<GridRows | undefined>(
    undefined
  )
  const [monthColumnIds, setMonthColumnIds] = useState<MonthColumnIds>({
    prognosis: [],
    outcome: [],
  })
  const [monthColumns, setMonthColumns] = useState<{
    [key: string]: MonthColumn
  }>({})

  const performanceGridRef = useRef<AgGridReact>(null)
  const [performanceRows, setPerformanceRows] = useState<GridRows | undefined>(
    undefined
  )
  const [performanceTotals, setPerformanceTotals] = useState<
    AggTranslations | undefined
  >(undefined)

  const [projectAttributes, setProjectAttributes] = useState<ProjectAttributes>(
    {
      buildStartMonth: '',
      startMonth: '',
      endMonth: '',
      mgmtBuildStartConsentPlanned: '',
      mgmtBuildStartConsent: '',
      finalInspectionPlanned: '',
      finalInspection: '',
      saleOfLandPlanned: '',
      saleOfLand: '',
      buildingCreditInterest: null,
      internalInterest: null,
      indexBaseMonth: '',
      indexPercentage: null,
    }
  )

  const [datesInfo, setDatesInfo] = useState<DatesInfo>({} as DatesInfo)
  const [baseCalculations, setBaseCalculations] = useState<BaseCalculations>({
    projectedCostsSubjectToVat: null,
    profitOnContract: null,
    profitOnContractLimit: null,
    expenditureOnCostsSubjectToVat: null,
  })

  const [lockedBudget, setLockedBudget] = useState<boolean>(false)
  const [initiallyUnsetBuildStartMonth, setInitiallyUnsetBuildStartMonth] =
    useState<boolean>(false)

  // Kontofilter state
  const [selectedCashflowAccountIds, setSelectedCashflowAccountIds] = useState<
    Set<number>
  >(new Set())
  const [selectedPerformanceAccountIds, setSelectedPerformanceAccountIds] =
    useState<Set<number>>(new Set())
  const [filterActive, setFilterActive] = useState<boolean>(true)

  // Diff filtering - common for both cashflow and performance
  const [minDiff, setMinDiff] = useState<number>(500)
  const [enableDiffFilter, setEnableDiffFilter] = useState<boolean>(false)
  const [enableCommentFilter, setEnableCommentFilter] = useState<boolean>(true)

  // Header aggregering toggle
  const [hideHeaderAggregation, setHideHeaderAggregation] =
    useState<boolean>(true)

  const value = {
    cashflowGridRef,
    cashflowRows,
    setCashflowRows,
    monthColumns,
    setMonthColumns,
    monthColumnIds,
    setMonthColumnIds,

    performanceGridRef,
    performanceRows,
    setPerformanceRows,
    performanceTotals,
    setPerformanceTotals,

    projectAttributes,
    setProjectAttributes,
    datesInfo,
    setDatesInfo,
    baseCalculations,
    setBaseCalculations,

    lockedBudget,
    setLockedBudget,
    initiallyUnsetBuildStartMonth,
    setInitiallyUnsetBuildStartMonth,

    selectedCashflowAccountIds,
    setSelectedCashflowAccountIds,
    selectedPerformanceAccountIds,
    setSelectedPerformanceAccountIds,
    filterActive,
    setFilterActive,

    minDiff,
    setMinDiff,
    enableDiffFilter,
    setEnableDiffFilter,
    enableCommentFilter,
    setEnableCommentFilter,

    hideHeaderAggregation,
    setHideHeaderAggregation,
  }

  return (
    <ProjektprognosSheetContext.Provider value={value}>
      {children}
    </ProjektprognosSheetContext.Provider>
  )
}

export const useProjektprognosSheetContext = () => {
  const context = useContext(ProjektprognosSheetContext)

  if (!context) {
    throw Error(
      'useProjektprognosSheetContext must be used within a ProjektprognosSheetContextProvider'
    )
  }

  return context
}
